"use client";

import { useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
import { withContactDetails } from "@/data/knowledge/company";
import { useTranslation } from "@/lib/i18n";
import { useConversation } from "@/lib/chat/conversation";
import ActionHandoff, { actionAnnouncement } from "./ActionHandoff";
import AnswerText from "./AnswerText";

/**
 * The conversation itself. Scrolls within the panel so the header, composer
 * and CTA stay put — see specs/ai-assistant — "Newest message stays in view".
 *
 * DELIBERATE DEVIATION from the Penpot boards. The drafts render an assistant
 * answer two different ways: board 11 (open inline) uses an avatar plus bare
 * text, board 12 (docked) uses a bordered bubble with no avatar — a leftover
 * from the older corner-widget boards 03/04. Since the whole premise is one
 * component in four appearances, rendering the same message two ways depending
 * on width contradicts it. Board 11's treatment is used everywhere: it is the
 * primary screen, and bare text reads better for the long answers this
 * assistant actually produces. Everything else on board 12 — paddings, radii,
 * borders, the 6 x 14 caret at 60% mint — is matched exactly.
 */
export default function MessageList({
  compact,
  narrow,
}: {
  compact: boolean;
  /** Below AssistantHost's `isNarrow`: handoffs drop the avatar indent. */
  narrow: boolean;
}) {
  const { t, locale } = useTranslation();
  const { messages, isStreaming } = useConversation();
  const endRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const handoffRef = useRef<HTMLDivElement>(null);

  // A NEW message is worth animating to.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  // So is an action attaching to the last answer: it arrives after the text,
  // when nothing else scrolls, and a form can be taller than the area left
  // for messages. "nearest" shows a form that fits whole, and the top of one
  // that does not.
  const last = messages[messages.length - 1];
  const lastAction = last?.action;
  const announced = lastAction ? actionAnnouncement(lastAction, t, locale) : null;
  useEffect(() => {
    if (!lastAction) return;
    handoffRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [lastAction]);

  // Text arriving into the current answer is not: the reveal updates every
  // frame, and starting a smooth scroll each time queues sixty competing
  // animations a second. Pin the container instead — instant and cheap.
  useEffect(() => {
    if (!isStreaming) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  const text = compact ? "text-xs" : "text-sm";
  const pad = compact ? "px-3 py-2" : "px-3.5 py-2.5";

  return (
    <div
      ref={listRef}
      className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${
        compact ? "gap-3 p-4" : "gap-3.5 p-5"
      }`}
    >
      {messages.map((msg) =>
        msg.role === "user" ? (
          <div key={msg.id} className="flex justify-end">
            <div
              className={`max-w-[85%] rounded-lg border border-mint-text/25 bg-mint/12 ${pad} ${text} leading-relaxed whitespace-pre-wrap text-text-primary`}
            >
              {msg.content}
            </div>
          </div>
        ) : (
          <div key={msg.id} className="flex items-start gap-3">
            {/*
              The avatar is the progress indicator. While the answer is being
              written it sends a ring out every beat and the mark inside turns
              over; when the answer settles both simply stop. Nothing is added
              to or removed from the layout, so the text below never shifts.
            */}
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-assistant-field ${
                msg.isStreaming
                  ? "assistant-thinking border-mint-text/40"
                  : "border-assistant-divider"
              }`}
              aria-hidden="true"
            >
              <Sparkles
                size={14}
                className={`text-mint-text ${
                  msg.isStreaming ? "assistant-spark-busy" : ""
                }`}
              />
            </div>
            <div className={`min-w-0 flex-1 ${text} leading-relaxed text-text-secondary`}>
              {/*
                An empty message means one of two things, and they must not look
                alike: the answer is still on its way, or it never came. Keying
                the indicator on `isStreaming` rather than on emptiness alone is
                what stops a failed answer sitting under "Schreibt…" forever.
              */}
              {msg.content ? (
                <AnswerText text={msg.content} />
              ) : msg.isStreaming ? (
                <span className="flex items-center gap-2 text-text-muted italic">
                  {/*
                    The moving dots ARE the ellipsis, so the written one is
                    dropped rather than sitting next to them — the label is
                    trimmed here instead of in the translations so both locales
                    keep a complete sentence for anything that reads them.
                  */}
                  {t("chat.typing").replace(/[.\u2026]+$/, "")}
                  <span className="flex items-center gap-1" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="assistant-dot h-1.5 w-1.5 rounded-full bg-mint-text"
                        style={{ animationDelay: `${i * 0.16}s` }}
                      />
                    ))}
                  </span>
                </span>
              ) : (
                <span>{withContactDetails(t("chat.unavailable"))}</span>
              )}
              {msg.isStreaming && msg.content && (
                <span
                  className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-mint-text/60 align-text-bottom"
                  aria-hidden="true"
                />
              )}
              {msg.action && (
                /*
                  On a narrow viewport the 40px avatar indent (w-7 + gap-3) is
                  given back to the handoff: a form there is short of width
                  already, and the avatar column is empty beside it.
                */
                <div
                  ref={msg === last ? handoffRef : undefined}
                  className={narrow ? "mt-3 -ml-10" : "mt-3"}
                >
                  <ActionHandoff action={msg.action} compact={compact} />
                </div>
              )}
            </div>
          </div>
        )
      )}


      {/*
        Answers are announced without moving focus. Only the settled text is
        published, so assistive technology is not spammed token by token. An
        action arrives after the text, so its line is an addition to the
        region and is read on its own.
      */}
      <div aria-live="polite" aria-atomic="false" className="sr-only">
        {!isStreaming && last?.role === "assistant" && (
          <>
            {last.content}
            {announced && <span> {announced}</span>}
          </>
        )}
      </div>

      <div ref={endRef} />
    </div>
  );
}
